import { orderBy } from 'lodash-es'
import pluralize from 'pluralize'
import reactStringReplace from 'react-string-replace'

import { FvButton, Icon } from '@fv/client-components'

import InfoBox from '@/components/InfoBox'
import { ListWithOxfordComma } from '@/components/ListWithOxfordComma'
import { Loading } from '@/components/Loading'

import { NoteTooltip } from './NoteTooltip'
import { useNmfcArticleSearch } from './queries'
import type {
  NmfcArticleResponse,
  OnReferenceChangeArgs,
  OnSelectArgs,
} from './types'
import { formatSubNumber, isUnshippable } from './utils'

type BaseProps = {
  code: string
  onSelect: (args: OnSelectArgs) => void
  leftAligned?: boolean
}
type ArticleDetailsQueryProps = BaseProps
export const ArticleDetailsQuery = ({
  code,
  onSelect,
}: ArticleDetailsQueryProps) => {
  const searchArticle = useNmfcArticleSearch({ code })
  if (searchArticle.isLoading) return <Loading className="mt-4" />

  if (!searchArticle.data) {
    return <div>No results found</div>
  }

  return (
    <ArticleDetails
      code={code}
      details={searchArticle.data}
      onSelect={onSelect}
    />
  )
}
type ArticleDetailsProps = BaseProps & {
  details: NmfcArticleResponse
  onReferenceChange?: (args: OnReferenceChangeArgs) => void
}

export const ArticleDetails = ({
  code,
  details,
  onSelect,
  onReferenceChange,
  leftAligned,
}: ArticleDetailsProps) => {
  const descriptionParagraph = (
    <p className="mb-0 bg-fv-beer-xlight p-4 border border-fv-gray border-b-0">
      {reactStringReplace(
        details.description,
        /see note[s,]* item[s]* ([(\d ,and]+)/i,
        match => {
          const tooltips = match
            .split(/,|and/g)
            .map(m => m.trim())
            .filter(Boolean)
            .map(m => <NoteTooltip key={m} nmfcNumber={m} />)

          return (
            <>
              {`see ${pluralize('note', tooltips.length)}, ${pluralize('item', tooltips.length)} `}
              <ListWithOxfordComma items={tooltips} />
              {match.trim().endsWith(',') ? ', ' : ''}
            </>
          )
        },
      )}
    </p>
  )

  if (details.isCanceled) {
    return (
      <InfoBox>
        This NMFC has been canceled.{' '}
        {details.canceledReference && onReferenceChange && (
          <>
            It has been replaced with{' '}
            <FvButton
              theme="underlined"
              onClick={() =>
                onReferenceChange({
                  code: details.canceledReference,
                })
              }
            >
              {details.canceledReference}
            </FvButton>
          </>
        )}
      </InfoBox>
    )
  }

  if (isUnshippable(details)) {
    return (
      <>
        {descriptionParagraph}
        <InfoBox>This is not a shippable NMFC code.</InfoBox>
      </>
    )
  }

  const items = orderBy(
    details.subItems.map(item => ({
      ...item,
      fullNmfcCode: [code, formatSubNumber(item.subNumber)].join('-'),
    })),
    ['subNumber'],
    ['asc'],
  )

  if (!items.length) {
    return (
      <>
        {descriptionParagraph}
        <InfoBox className="">
          The freight class for{' '}
          <FvButton
            theme="underlined"
            onClick={() =>
              onSelect({
                nmfcCode: code,
                description: processDescriptionForChange(details.description),
                freightClass: details.freightClass,
              })
            }
          >
            this article is {details.freightClass}
          </FvButton>
          <span className="italic text-sm">
            <Icon icon="arrow-left" className="mx-2 text-fv-gray-500" />
            (select to use)
          </span>
        </InfoBox>
      </>
    )
  }

  return (
    <>
      <div>{descriptionParagraph}</div>
      <div>
        <table className="w-full mb-6">
          <tr className="[&_td]:px-4 [&_td]:py-2 border bg-fv-blue-50 border-fv-blue-200">
            <td>Sub</td>
            <td className="whitespace-nowrap text-center">Class</td>
            <td className="whitespace-nowrap text-center">NMFC</td>
            <td className="whitespace-nowrap">&nbsp;</td>
          </tr>

          {items.map(subItem => (
            <tr
              key={subItem.fullNmfcCode}
              className="[&_td]:px-4 [&_td]:py-2 border border-fv-gray odd:bg-fv-gray-100"
            >
              <td>
                <div>Sub {subItem.subNumber}</div>
                <div className="text-sm italic">{subItem.description}</div>
              </td>
              <td className="whitespace-nowrap text-center">
                {subItem.freightClass}
              </td>
              <td className="whitespace-nowrap text-center">
                {subItem.fullNmfcCode}
              </td>
              <td className="text-right">
                <FvButton
                  icon="check"
                  theme="default"
                  className="!inline-block"
                  onClick={() =>
                    onSelect({
                      nmfcCode: subItem.fullNmfcCode,
                      description: processDescriptionForChange(
                        subItem.description,
                      ),
                      freightClass: subItem.freightClass,
                    })
                  }
                >
                  Use this
                </FvButton>
              </td>
            </tr>
          ))}
        </table>
      </div>
    </>
  )
}

const processDescriptionForChange = (description?: string) => {
  if (!description) return description
  if (description.length <= 100) return description
  return `${description.substring(0, 100)}...`
}
